import { Box, Button, Card, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ComputerIcon from '@mui/icons-material/Computer';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import { Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { CLIENT_EDR_ENDPOINTS_DEPLOY_AGENT } from 'src/constants/constants';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import useHasPermissions from 'src/hooks/useHasPermission';
import { getSecurityDeployAgentHref, getSecurityEndpointDetailsHref } from 'src/mindtime/config/securityNav';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import { useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import { SecurityClientFilter } from '../shared/SecurityClientFilter';
import { SecurityEndpointFilter } from '../shared/SecurityEndpointFilter';
import { EdrDateTime } from '../shared/edrPrimitives';
import { SecurityDashboardToolbar, SecurityToolbarFilter } from '../shared/securityDashboardToolbar';
import { deriveAgentHealthLabel } from '../shared/wazuhDashboardUtils';
import { buildEndpointsApiScope } from '../shared/wazuhScopeParams';
import { mapWazuhEndpointItem } from './mapWazuhEndpoint';
import type { EndpointRisk, EndpointRow, EndpointStatus } from './types';
import { useEndpointsData, type EndpointsSortParams } from './useEndpointsData';

const cardSx = {
  border: '1px solid #E2E8F0',
  borderRadius: '10px',
  boxShadow: 'none',
  backgroundColor: '#FFFFFF',
} as const;

const STATUS_STYLES: Record<EndpointStatus, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: '#0D9488', bg: '#CCFBF1' },
  disconnected: { label: 'Disconnected', color: '#B45309', bg: '#FEF3C7' },
  never_connected: { label: 'Never connected', color: '#475569', bg: '#F1F5F9' },
  offline: { label: 'Offline', color: '#DC2626', bg: '#FEE2E2' },
};

const RISK_STYLES: Record<EndpointRisk, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: '#16A34A', bg: '#DCFCE7' },
  medium: { label: 'Medium', color: '#CA8A04', bg: '#FEF9C3' },
  high: { label: 'High', color: '#EA580C', bg: '#FFEDD5' },
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEE2E2' },
};

const SYSTEM_OPTIONS = [
  { label: 'Windows', value: 'windows' },
  { label: 'Linux', value: 'linux' },
  { label: 'macOS', value: 'macos' },
];

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Disconnected', value: 'disconnected' },
  { label: 'Never connected', value: 'never_connected' },
];

const Pill: FC<{ label: string; color: string; bg?: string }> = ({ label, color, bg }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1,
      py: 0.25,
      borderRadius: '4px',
      fontSize: 11,
      fontWeight: 600,
      color,
      backgroundColor: bg ?? 'transparent',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

const StatCard: FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  iconBg: string;
}> = ({ title, value, subtitle, icon, iconBg }) => (
  <Card sx={cardSx} style={{ padding: '16px 18px', height: '100%' }}>
    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>{title}</Typography>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: iconBg,
        }}
      >
        {icon}
      </Box>
    </Box>
    <Typography style={{ fontSize: 28, fontWeight: 700, color: '#1E293B', lineHeight: 1.1 }}>
      {value}
    </Typography>
    {subtitle ? (
      <Typography style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>{subtitle}</Typography>
    ) : null}
  </Card>
);

const Endpoints = () => {
  const navigate = useNavigate();
  const checkPermissions = useHasPermissions();
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;

  const { filters, setFilter, search: searchQuery, searchInput, setSearchInput } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.endpoints, { debounceMs: false });
  const {
    organizationId = '',
    selectedEndpointId: selectedEndpointIdRaw = '',
    system = '',
    status = '',
  } = filters;
  const selectedEndpointId = selectedEndpointIdRaw || null;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sortParams, setSortParams] = useState<EndpointsSortParams | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [agentStats, setAgentStats] = useState({
    total: 0,
    healthy: 0,
    healthyPercent: 0,
    requiringAttention: 0,
    warningCount: 0,
    criticalCount: 0,
    offline: 0,
    totalDelta: '',
  });
  const [statsLoading, setStatsLoading] = useState(true);

  const overviewScope = useMemo(
    () =>
      buildEndpointsApiScope({
        scope,
        profileOrgId,
        isReseller,
        pageOrganizationId: organizationId || undefined,
      }),
    [scope, profileOrgId, isReseller, organizationId]
  );

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);

    wazuhAPI.dashboard
      .getAgentOverview(overviewScope)
      .then((data) => {
        if (cancelled) return;
        const { active, warning, offline } = data.agentStatus;
        const total = active + warning + offline;
        const healthyPercent = total > 0 ? Math.round((active / total) * 100) : 0;
        setAgentStats({
          total,
          healthy: active,
          healthyPercent,
          requiringAttention: warning,
          warningCount: warning,
          criticalCount: 0,
          offline,
          totalDelta: deriveAgentHealthLabel(active, warning, offline),
        });
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'Endpoints');
        }
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [overviewScope, refreshKey]);

  const { loading, items: paged } = useEndpointsData(
    page,
    rowsPerPage,
    searchQuery,
    {
      pageOrganizationId: organizationId || undefined,
      selectedEndpointId: selectedEndpointId ?? undefined,
      system,
      status,
    },
    refreshKey,
    sortParams
  );

  const tableRows = useMemo(
    () => (paged?.items ?? []).map(mapWazuhEndpointItem),
    [paged?.items]
  );

  const totalRecords = paged?.totalRecords ?? 0;

  const handleSort = (columnId: string, direction: 'asc' | 'desc' | null) => {
    setSortParams(direction ? { column: columnId, sort: direction } : null);
    setPage(0);
  };

  const handleDeleteSelected = async () => {
    const systemWazuhAgentIds = selectedRows.filter(Boolean).join(',');
    if (!systemWazuhAgentIds) return;

    setDeleting(true);
    try {
      await wazuhAPI.deleteAgents({ systemWazuhAgentIds });
      toast.success(
        selectedRows.length === 1
          ? 'Agent deleted'
          : `${selectedRows.length} agents deleted`
      );
      setSelectedRows([]);
      setRefreshKey((key) => key + 1);
    } catch (error) {
      logger(error, 'error', 'Endpoints');
      toast.error(error instanceof Error ? error.message : 'Failed to delete agents');
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'endpoint',
        label: 'Endpoint',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
            {(row as unknown as EndpointRow).endpoint}
          </Typography>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        render: (row) => {
          const { status: rowStatus } = row as unknown as EndpointRow;
          const { label, color, bg } = STATUS_STYLES[rowStatus] ?? STATUS_STYLES.active;
          return <Pill label={label} color={color} bg={bg} />;
        },
      },
      {
        id: 'risk',
        label: 'Risk',
        sortable: true,
        render: (row) => {
          const { risk } = row as unknown as EndpointRow;
          const { label, color, bg } = RISK_STYLES[risk];
          return <Pill label={label} color={color} bg={bg} />;
        },
      },
      {
        id: 'os',
        label: 'OS',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B' }}>
            {(row as unknown as EndpointRow).os}
          </Typography>
        ),
      },
      {
        id: 'vulnerabilities',
        label: 'Open Vulnerabilities',
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).vulnerabilities).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'criticalVulnerabilities',
        label: 'Critical Vulnerabilities',
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).criticalVulnerabilities).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'alerts',
        label: 'Alerts',
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).alerts).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'criticalAlerts',
        label: 'Critical Alerts',
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).criticalAlerts).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'openIssues',
        label: 'Open Issues',
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {String((row as unknown as EndpointRow).openIssues).padStart(2, '0')}
          </Typography>
        ),
      },
      {
        id: 'overallComplianceScore',
        label: 'Compliance Score',
        align: 'left',
        sortable: true,
        render: (row) => (
          <Typography style={{ fontSize: 12, color: '#64748B', textAlign: 'left' }}>
            {`${(row as unknown as EndpointRow).overallComplianceScore ?? 0}%`}
          </Typography>
        ),
      },
      {
        id: 'lastSeen',
        label: 'Last Seen',
        align: 'left',
        sortable: true,
        render: (row) => (
          <div style={{ textAlign: 'left' }}>
            <EdrDateTime value={(row as unknown as EndpointRow).lastSeen} />
          </div>
        ),
      },
    ],
    []
  );

  const stats = agentStats;

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Endpoints')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ minHeight: '100%', boxSizing: 'border-box', gap: 16 }}
      >
        <Typography
          component="h1"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 22,
            fontWeight: 700,
            color: '#1E293B',
          }}
        >
          Endpoints
        </Typography>

        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
          <Box style={{ flex: '1.6 1 280px', minWidth: 220 }}>
            <SecurityDashboardToolbar
              searchValue={searchInput}
              onSearchChange={(value) => {
                setSearchInput(value);
                setPage(0);
              }}
              searchPlaceholder="Search endpoints..."
              showClientFilter={false}
              layout="full"
            />
          </Box>
          {isReseller ? (
            <Box style={{ flex: '1 1 160px', minWidth: 150 }}>
              <SecurityClientFilter
                value={organizationId || null}
                onChange={(clientId) => {
                  setFilter('organizationId', clientId ?? '');
                  setFilter('selectedEndpointId', '');
                  setPage(0);
                }}
                pillLabel="Organization"
                allLabel="All Organization"
                searchPlaceholder="Search organizations..."
                emptyMessage="No organizations found"
                selectedFallbackLabel="Selected organization"
                showFilterIcon={false}
                fullWidth={false}
              />
            </Box>
          ) : null}
          <Box style={{ flex: '1 1 160px', minWidth: 150 }}>
            <SecurityEndpointFilter
              value={selectedEndpointId}
              onChange={(endpointId) => {
                setFilter('selectedEndpointId', endpointId ?? '');
                setPage(0);
              }}
              pageOrganizationId={organizationId || undefined}
            />
          </Box>
        </Box>

        <Box
          display="grid"
          style={{
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 16,
          }}
        >
          <StatCard
            title="Total Endpoints"
            value={statsLoading ? '—' : stats.total}
            subtitle={statsLoading ? undefined : stats.totalDelta}
            icon={<ComputerIcon style={{ fontSize: 18, color: '#3182CE' }} />}
            iconBg="#DBEAFE"
          />
          <StatCard
            title="Healthy Endpoints"
            value={statsLoading ? '—' : stats.healthy}
            subtitle={statsLoading ? undefined : `${stats.healthyPercent}%`}
            icon={<CheckCircleOutlineIcon style={{ fontSize: 18, color: '#16A34A' }} />}
            iconBg="#DCFCE7"
          />
          <StatCard
            title="Endpoints Requiring Attention"
            value={statsLoading ? '—' : stats.requiringAttention}
            subtitle={statsLoading ? undefined : `${stats.warningCount} Warning · ${stats.criticalCount} Critical`}
            icon={<ReportProblemOutlinedIcon style={{ fontSize: 18, color: '#DC2626' }} />}
            iconBg="#FEE2E2"
          />
          <StatCard
            title="Offline Endpoints"
            value={statsLoading ? '—' : stats.offline}
            icon={<WifiOffIcon style={{ fontSize: 18, color: '#64748B' }} />}
            iconBg="#F1F5F9"
          />
        </Box>

        <Card sx={cardSx} style={{ padding: '16px' }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            style={{ gap: 12, marginBottom: 12 }}
          >
            <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
              <SecurityToolbarFilter
                label="System"
                options={SYSTEM_OPTIONS}
                value={system}
                onChange={(value) => {
                  setFilter('system', value);
                  setPage(0);
                }}
                minWidth={140}
              />
              <SecurityToolbarFilter
                label="Status"
                options={STATUS_OPTIONS}
                value={status}
                onChange={(value) => {
                  setFilter('status', value);
                  setPage(0);
                }}
                minWidth={140}
              />
            </Box>

            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {selectedRows.length > 0 ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  disabled={deleting}
                  startIcon={<DeleteOutlineIcon style={{ fontSize: 16 }} />}
                  style={{ textTransform: 'none', fontSize: 13 }}
                  onClick={handleDeleteSelected}
                >
                  {deleting ? 'Deleting…' : `Delete (${selectedRows.length})`}
                </Button>
              ) : null}
              {checkPermissions([CLIENT_EDR_ENDPOINTS_DEPLOY_AGENT]) && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon style={{ fontSize: 16 }} />}
                  style={{ textTransform: 'none', fontSize: 13 }}
                  onClick={() => navigate(getSecurityDeployAgentHref())}
                >
                  Deploy New Agent
                </Button>
              )}
            </Box>
          </Box>

          <Table
            columns={columns}
            data={tableRows as unknown as TableData[]}
            loading={loading}
            showCheckbox
            selectedRows={selectedRows}
            onSelectRows={setSelectedRows}
            isRowSelectable={(row) =>
              (row as unknown as EndpointRow).status === 'never_connected'
            }
            onRowClick={(row) => {
              const endpointRow = row as unknown as EndpointRow;
              const systemWazuhAgentId = endpointRow.id?.trim();
              if (!systemWazuhAgentId) return;
              navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
                state: { endpointName: endpointRow.endpoint },
              });
            }}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(nextRowsPerPage) => {
              setRowsPerPage(nextRowsPerPage);
              setPage(0);
            }}
            onSort={handleSort}
            showPagination
          />
        </Card>
      </Box>
    </>
  );
};

export default Endpoints;
